"use client";

import { FormEvent, useState } from "react";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/analytics";
import { deriveLocaleFromPath } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

type Status = "idle" | "submitting" | "success" | "error";

const inputClass =
  "w-full rounded-2xl bg-white/40 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 ring-1 ring-white/30 backdrop-blur-xl outline-none focus:ring-2 focus:ring-cyan-500/30 dark:bg-white/10 dark:text-white dark:placeholder:text-white/40 dark:ring-white/15 dark:focus:ring-white/30";

export function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname() || "/";
  const { locale } = deriveLocaleFromPath(pathname);
  const m = getMessages(locale).contactForm;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "submitting") return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();
    const website = String(data.get("website") ?? "").trim();

    setStatus("submitting");
    setError(null);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, website }),
      });

      const payload = (await res.json().catch(() => null)) as
        | { error?: string; ok?: boolean }
        | null;

      if (!res.ok) {
        throw new Error(payload?.error || m.errorFallback);
      }

      setStatus("success");
      trackEvent("generate_lead", {
        form_type: "contact",
        form_name: "contact_message",
      });
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : m.errorFallback);
    }
  }

  return (
    <div className="rounded-[2rem] bg-white/30 p-6 ring-1 ring-white/20 backdrop-blur-xl dark:bg-black/20 dark:ring-white/10">
      <div className="text-sm font-semibold text-slate-900 dark:text-white">{m.title}</div>
      <p className="mt-1 text-xs text-slate-600 dark:text-white/70">{m.subtitle}</p>

      {status === "success" ? (
        <div
          className="mt-5 rounded-2xl bg-emerald-500/15 px-4 py-3 text-sm text-emerald-800 ring-1 ring-emerald-500/25 dark:text-emerald-200"
          role="status"
        >
          {m.success}
        </div>
      ) : null}

      <form className="mt-5 grid gap-3" onSubmit={handleSubmit} noValidate>
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-0 w-0 opacity-0"
        />
        <input
          className={inputClass}
          placeholder={m.name}
          name="name"
          type="text"
          required
          aria-required
          maxLength={200}
          disabled={status === "submitting"}
        />
        <input
          className={inputClass}
          placeholder={m.email}
          name="email"
          type="email"
          required
          aria-required
          maxLength={320}
          disabled={status === "submitting"}
        />
        <textarea
          className={`min-h-[120px] resize-none ${inputClass}`}
          placeholder={m.message}
          name="message"
          required
          aria-required
          maxLength={5000}
          disabled={status === "submitting"}
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="rounded-2xl bg-[#27598e]/90 px-4 py-3 text-sm font-semibold text-white backdrop-blur-sm hover:bg-[#27598e] disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-black dark:hover:bg-white/90"
        >
          {status === "submitting" ? m.sending : m.send}
        </button>
        {error ? (
          <div className="text-xs text-red-600 dark:text-red-300" role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </div>
  );
}
